import { Link, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { OptionDialog } from "~/components/ui/option-dialog";
import {
    useLocalePreference,
    useLocalizedPaths,
    LANGUAGE_ITEMS,
    selectedLanguageId,
    localizedPath,
} from "~/lib/use-locale-preference";
import { useCurrencyPreference, type SupportedCurrency } from "~/lib/use-currency-preference";
import { pushCurrencyToEmbeddedCart, pushLanguageToEmbeddedCart } from "~/lib/use-embedded-cart";

// Currency is independent of language/locale — see use-currency-preference.ts.
// MXN has no pricing catalog data yet (checkoutdwa only serves usd/cad) — shown as a
// disabled placeholder for the eventual direction, not a selectable option. It's
// deliberately not part of SupportedCurrency (nothing functional should accept it), so
// this list needs its own slightly wider type just for what can be displayed.
type DisplayCurrency = SupportedCurrency | "mxn";

const CURRENCY_ITEMS = [
    { currency: "usd", title: "United States dollar", symbol: "$", disabled: false },
    { currency: "cad", title: "Canadian dollar", symbol: "$", disabled: false },
    { currency: "mxn", title: "Mexican peso", symbol: "$", disabled: true },
] as const satisfies ReadonlyArray<{ currency: DisplayCurrency; title: string; symbol: string; disabled: boolean }>;

export function LocaleDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
    const { t } = useTranslation("common");
    const { pathname } = useLocation();
    const localizedPaths = useLocalizedPaths();
    const [locale, setLocale] = useLocalePreference();
    const [currency, setCurrency] = useCurrencyPreference();

    const languageId = selectedLanguageId(pathname, locale);

    return (
        <OptionDialog
            open={open}
            onOpenChange={onOpenChange}
            title={t("locale_dialog.title")}
            hideTitle
            className="w-full max-w-2xl gap-4 pb-12"
            tabs={[
                {
                    value: "language",
                    label: t("locale_dialog.tab_language"),
                    items: LANGUAGE_ITEMS.map((item) => ({
                        title: item.title,
                        subtext: item.region,
                        selected: languageId === item.id,
                        as: Link,
                        to: localizedPath(pathname, item.path, localizedPaths),
                        onClick: () => {
                            setLocale(item.id);
                            pushLanguageToEmbeddedCart(item.id);
                            onOpenChange(false);
                        },
                    })),
                },
                {
                    value: "currency",
                    label: t("locale_dialog.tab_currency"),
                    items: CURRENCY_ITEMS.map((item) => ({
                        title: item.title,
                        subtext: `${item.currency.toUpperCase()} - ${item.symbol}`,
                        selected: currency === item.currency,
                        disabled: item.disabled,
                        badge: item.disabled ? t("locale_dialog.coming_soon") : undefined,
                        onClick: () => {
                            // Never actually reachable — NavItem strips onClick when disabled — but
                            // this also narrows item.currency from DisplayCurrency to SupportedCurrency.
                            if (item.disabled) return;
                            setCurrency(item.currency);
                            pushCurrencyToEmbeddedCart(item.currency);
                        },
                    })),
                },
            ]}
        />
    );
}
